/**
 * Shop the Look — mobile swipe navigation
 *
 * Drives the <shop-the-look-element> carousel directly via its public API
 * (active_index + updateActiveProduct()) on a horizontal swipe. The component
 * binds its prev/next buttons with a custom .on() method rather than native
 * events, so synthesizing arrow clicks does nothing; calling the public method
 * is the reliable path, and it also triggers the component's own image-height
 * recalculation and hotspot highlighting.
 *
 * Progressive enhancement, touch only, scoped to <= 767px (theme small
 * breakpoint), re-evaluated on breakpoint change. Prefers the theme's bundled
 * SwipeListener if exposed on window; native touch fallback otherwise. Never
 * blocks vertical scroll, and suppresses the stray link tap a swipe would fire
 * on a product card. Index wraps at both ends.
 *
 * Install: add as assets/shop-the-look-swipe.js and reference it with defer,
 * or append the contents to an existing custom theme script. Self-guards on
 * pages without the section. (You already added the asset + tag, so just
 * replace the asset contents with this.)
 */
(() => {
  const MOBILE_MQ = '(max-width: 767px)';
  const SWIPE_THRESHOLD = 40; // px of horizontal travel before we navigate
  let teardowns = [];

  const productCount = (el) =>
    el.products?.length ?? el.querySelectorAll('.shop-the-look--product').length;

  /** Move the carousel by +1 / -1 with wrap-around, via the component's own API. */
  const step = (el, delta) => {
    const count = productCount(el);
    if (count < 2 || typeof el.updateActiveProduct !== 'function') return;
    const current = el.active_index ?? 0;
    el.active_index = (current + delta + count) % count;
    el.updateActiveProduct();
  };

  /** Stop the swipe gesture from also activating a product-card link. */
  const suppressNextClick = (el) => {
    const onClick = (event) => {
      event.preventDefault();
      event.stopPropagation();
      el.removeEventListener('click', onClick, true);
    };
    el.addEventListener('click', onClick, true);
    setTimeout(() => el.removeEventListener('click', onClick, true), 400);
  };

  const bindWithSwipeListener = (el, target) => {
    const instance = window.SwipeListener(target, {
      minHorizontal: SWIPE_THRESHOLD,
      deltaHorizontal: 12,
      preventScroll: false, // never block vertical page scroll
      lockAxis: true,
      mouse: false, // touch only
    });

    const onSwipe = (event) => {
      const dirs = event.detail?.directions;
      if (!dirs) return;
      if (dirs.left) {
        suppressNextClick(el);
        step(el, 1);
      } else if (dirs.right) {
        suppressNextClick(el);
        step(el, -1);
      }
    };

    target.addEventListener('swipe', onSwipe);
    return () => {
      target.removeEventListener('swipe', onSwipe);
      instance?.off?.();
    };
  };

  const bindNative = (el, target) => {
    let startX = 0;
    let startY = 0;
    let tracking = false;

    const onStart = (event) => {
      const touch = event.changedTouches[0];
      startX = touch.clientX;
      startY = touch.clientY;
      tracking = true;
    };

    const onEnd = (event) => {
      if (!tracking) return;
      tracking = false;
      const touch = event.changedTouches[0];
      const dx = touch.clientX - startX;
      const dy = touch.clientY - startY;
      // Ignore taps, and let vertical scroll win when it dominates.
      if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) <= Math.abs(dy)) return;
      suppressNextClick(el);
      step(el, dx < 0 ? 1 : -1);
    };

    // passive listeners — we never preventDefault, so scrolling is untouched.
    target.addEventListener('touchstart', onStart, { passive: true });
    target.addEventListener('touchend', onEnd, { passive: true });
    return () => {
      target.removeEventListener('touchstart', onStart);
      target.removeEventListener('touchend', onEnd);
    };
  };

  const enhance = (el) => {
    if (productCount(el) < 2) return;
    if (el.dataset.swipeBound === 'true') return;
    el.dataset.swipeBound = 'true';

    // Swipe anywhere over the image + products block.
    const target = el.querySelector('.shop-the-look--block') ?? el;
    const teardown = typeof window.SwipeListener === 'function'
      ? bindWithSwipeListener(el, target)
      : bindNative(el, target);

    teardowns.push(() => {
      teardown();
      delete el.dataset.swipeBound;
    });
  };

  const activate = () => {
    for (const el of document.querySelectorAll('shop-the-look-element')) enhance(el);
  };

  const deactivate = () => {
    for (const teardown of teardowns) {
      try {
        teardown();
      } catch {
        /* no-op */
      }
    }
    teardowns = [];
  };

  const sync = (mq) => {
    deactivate();
    if (mq.matches) activate();
  };

  const init = () => {
    const mq = window.matchMedia(MOBILE_MQ);
    sync(mq);
    mq.addEventListener?.('change', () => sync(mq));
  };

  // Wait until the component is defined so active_index / updateActiveProduct exist.
  if (window.customElements?.get('shop-the-look-element')) {
    init();
  } else {
    window.customElements?.whenDefined('shop-the-look-element').then(init);
  }
})();